import { Log, UserManager, WebStorageStateStore } from "oidc-client";
import { Authserverconstants } from "../utils/AuthServerConstants";

export default class AuthService {
    UserManager;
    authConfig: any;

    constructor() {
        this.authConfig = this.getAuthConfig();
        const IDENTITY_CONFIG = this.getIdentityConfiguration();
        const METADATA_OIDC = this.getMetaDataOIDC();
        this.UserManager = new UserManager({
            ...IDENTITY_CONFIG,
            userStore: new WebStorageStateStore({ store: window.sessionStorage }),
            metadata: {
                ...METADATA_OIDC,
            },
        });
        // Logger
        Log.logger = console;
        Log.level = Log.DEBUG;
        this.UserManager.events.addUserLoaded((user: any) => {
            if (window.location.href.indexOf("signin-oidc") !== -1) {
                console.log(user);
                localStorage.setItem("loggedInUser", JSON.stringify(user));
                this.navigateToScreen();
            }
        });
        this.UserManager.events.addSilentRenewError((e: any) => {
            console.log("silent renew error", e.message);
        });

        this.UserManager.events.addAccessTokenExpired(() => {
            console.log("token expired");
            this.logout();
        });
    }

    getAuthConfig() {
        const storedAuthConfig = localStorage.getItem('authConfig');
        if (storedAuthConfig) {
            return JSON.parse(storedAuthConfig);
        }
    }

    getLoggedInUserDetails() {
        let user = localStorage.getItem("loggedInUser");
        if (user) {
            return JSON.parse(user);
        }
    }

    //To get auth server configuration
    getIdentityConfiguration = () => {
        return {
            authority: this.authConfig.authServerUrl,
            client_id: this.authConfig.clientId,
            redirect_uri: `${window.location.origin}/signin-oidc`,
            response_type: this.authConfig.responseType,
            scope: this.authConfig.scope,
            post_logout_redirect_uri: `${window.location.origin}/signout-oidc`,
        };
    };

    //To get auth server meta data information
    getMetaDataOIDC = () => {
        let authServerUrl = this.authConfig.authServerUrl;
        return {
            issuer: authServerUrl,
            jwks_uri: authServerUrl + "/.well-known/openid-configuration/jwks",
            authorization_endpoint: authServerUrl + "/connect/authorize",
            token_endpoint: authServerUrl + "/connect/token",
            userinfo_endpoint: authServerUrl + "/connect/userinfo",
            end_session_endpoint: authServerUrl + "/connect/endsession",
            check_session_iframe: authServerUrl + "/connect/checksession",
            revocation_endpoint: authServerUrl + "/connect/revocation",
            introspection_endpoint: authServerUrl + "/connect/introspect",
        };
    };

    signinRedirectCallback = () => {
        this.UserManager.signinRedirectCallback()
            .then((user: any) => {
            })
            .catch(function (e: any) {
                console.error(e);
            });
    };

    getUser = async () => {
        const user = await this.UserManager.getUser();
        if (!user) {
            return await this.UserManager.signinRedirectCallback();
        }
        return user;
    };

    parseJwt = (token: string) => {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace("-", "+").replace("_", "/");
        return JSON.parse(window.atob(base64));
    };

    signinRedirect = () => {
        localStorage.setItem("redirectUri", window.location.pathname);
        this.UserManager.signinRedirect({});
    };

    navigateToScreen = () => {
        const redirectUri = localStorage.getItem("redirectUri");
        window.location.replace(redirectUri ?? "/");
    };

    isAuthenticated = () => {
        let token = sessionStorage.getItem(localStorage.getItem('accessTokenVariable') ?? "");
        if (token) {
            const oidcStorage = JSON.parse(token);
            return !!oidcStorage && !!oidcStorage.access_token;
        }
        return false;
    };

    isAuthorised = () => {
        return (this.isAuthenticated() && (this.isGpDiaryAdmin() || this.isGpDiaryMember() || this.isGpDiaryStaff()));
    }

    signinSilent = () => {
        this.UserManager.signinSilent()
            .then((user: any) => {
                console.log("signed in", user);
            })
            .catch((err: any) => {
                console.log(err);
            });
    };
    signinSilentCallback = () => {
        this.UserManager.signinSilentCallback();
    };

    createSigninRequest = () => {
        return this.UserManager.createSigninRequest();
    };

    logout = () => {
        this.UserManager.signoutRedirect({
            id_token_hint: localStorage.getItem("id_token"),
        });
        this.UserManager.clearStaleState();
    };

    signoutRedirectCallback = () => {
        this.UserManager.signoutRedirectCallback().then(() => {
            localStorage.clear();
            sessionStorage.clear();
            window.location.replace("/");
        });
        this.UserManager.clearStaleState();
    };


    isGpDiaryAdmin() {
        const user = this.getLoggedInUserDetails();
        if (user && user.profile && user.profile.role) {
            const isAdmin = user.profile.role.includes(Authserverconstants.Claim_Role_Admin_Value);
            return isAdmin;
        }
        return false;
    }

    isTestMode() {
        const authConfig = this.getAuthConfig();
        if (authConfig) {
            return authConfig.isTestMode;
        }
    }

    isTestMember() {
        const user = this.getLoggedInUserDetails();
        if (user?.profile?.role) {
            return user?.profile?.role?.includes(Authserverconstants.Claim_Role_Test_Member_Value);
        }
        return false;
    }

    isGpDiaryMember() {
        const user = this.getLoggedInUserDetails();
        if (user && user.profile && user.profile.user_type) {
            const isMember = user.profile.user_type == Authserverconstants.Claim_User_Type_Member_Value && user.profile.status == Authserverconstants.Claim_User_Profile_Status_Value;
            const isAllowed = this.isTestMode() == 'true' ? this.isTestMember() : true;
            if (isMember && isAllowed) {
                return true;
            }
            return false;
        }
        return false;
    }

    isGpDiaryStaff() {
        const user = this.getLoggedInUserDetails();
        if (user && user.profile && user.profile.user_type) {
            const isStaff = user.profile.user_type == Authserverconstants.Claim_User_Type_Staff_Value;
            const isAllowed = this.isTestMode() == 'true' ? this.isTestMember() : true;
            if (isStaff && isAllowed) {
                return true;
            }
            return false;
        }
        return false;
    }

    getLoggedInUserName() {
        const user = this.getLoggedInUserDetails();
        if (user && user.profile) {
            let userName = "";
            userName += user.profile.first_name ? user.profile.first_name : "";
            userName += " ";
            userName += user.profile.last_name ? user.profile.last_name : "";
            return userName;
        }
        return "";
    }

    //To get diary popover details to show or hide 
    getIsDiaryPopoverIsShown() {
        if (localStorage.getItem('isDiaryPopoverSeen')) {
            return true;
        }
        return false;
    }

    //To get report popover details to show or hide
    getIsReportPopoverIsShown = () => {
        if (localStorage.getItem('isReportPopoverSeen')) {
            return true;
        }
        return false;
    }

    //To set diary popover details to show or hide
    setIsDiaryPopoverIsShown() {
        localStorage.setItem("isDiaryPopoverSeen","true");
    }

    //To set report popover details to show or hide
    setIsReportPopoverIsShown = () => {
        localStorage.setItem("isReportPopoverSeen","true");
    }
}
